import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { formatNr2jDate } from '../../utils/nr2jConstants';

function toDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Modal de planification de relance NR2J.
 * Gère FOLLOWUP_EXISTS via existingConflict (existingTask + message).
 */
export default function Nr2jPlanFollowUpModal({
  open,
  busy = false,
  organizationName,
  existingConflict = null,
  onClose,
  onSubmit,
  onUpdateExisting,
}) {
  const [dueAt, setDueAt] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    if (existingConflict?.existingTask?.dueAt) {
      setDueAt(toDatetimeLocal(existingConflict.existingTask.dueAt));
      setNotes(existingConflict.existingTask.notes || '');
    } else {
      setDueAt('');
      setNotes('');
    }
  }, [open, organizationName, existingConflict]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="nr2j-plan-followup-modal">
      <button type="button" className="absolute inset-0 bg-black/30" aria-label="Fermer" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Planifier une relance</h3>
            {organizationName ? (
              <p className="mt-0.5 text-sm text-gray-500">{organizationName}</p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-50">
            <X className="h-4 w-4" />
          </button>
        </div>

        {existingConflict ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-amber-900">
              {existingConflict.message
                || `Une relance est déjà planifiée le ${formatNr2jDate(existingConflict.existingTask?.dueAt)}.`}
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                disabled={busy || !dueAt}
                onClick={() => onUpdateExisting?.({ dueAt: new Date(dueAt).toISOString(), notes: notes || null })}
                className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                Modifier cette relance
              </button>
              <label className="block text-sm">
                <span className="text-gray-600">Nouvelle date / heure</span>
                <input
                  type="datetime-local"
                  required
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">Note (optionnel)</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
              <button type="button" onClick={onClose} className="rounded-lg border px-3 py-2 text-sm">
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <form
            className="mt-4 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!dueAt) return;
              onSubmit?.({ dueAt: new Date(dueAt).toISOString(), notes: notes || null });
            }}
          >
            <label className="block text-sm">
              <span className="font-medium text-gray-700">Date et heure</span>
              <input
                type="datetime-local"
                required
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700">Note (optionnel)</span>
              <textarea
                rows={3}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex. Rappeler après devis envoyé"
              />
            </label>
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={busy || !dueAt}
                className="flex-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {busy ? 'Enregistrement…' : 'Planifier'}
              </button>
              <button type="button" onClick={onClose} className="rounded-lg border px-3 py-2 text-sm">
                Annuler
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export { toDatetimeLocal };
